/**
 * **The client's week**, drawn beside the three task kinds that are about a client's rhythm rather
 * than about one object: a missed workout, a lapse, an empty calendar.
 *
 * Seven squares Monday to Sunday, and the four-week attendance run under them. The day the task is
 * about is marked, so the card answers the question the row raises — *is this one slip or is this
 * the third week of them?* — without the trainer opening the client.
 *
 * ── WHICH week: the one the TASK is about ────────────────────────────────────────────────────
 * A missed workout names a day, and that day is the subject of the card. Drawing "this week" beside
 * it answers a question nobody asked: a session missed on 2 September, read on the 19th, put seven
 * squares of the wrong fortnight under a sentence about the 2nd, and the marked square was not even
 * in the strip. The strip therefore ANCHORS on the task's day where the reason names one, and the
 * attendance run is the four weeks ENDING with that week — the run reads up to the slip, which is
 * the comparison the trainer is making.
 *
 * `lapsedActivity` and `noUpcomingSessions` name no day. They are statements about now, so they keep
 * the current week, and `anchorIsCurrentWeek` is what lets the surface title it "Ten tydzień"
 * instead of a date range. The title is never "this week" for a week that is not.
 *
 * ── Why the first version drew nothing against the real backend ──────────────────────────────
 * It compared `displayDate` to a `YYYY-MM-DD` square. `DisplayDate` is a `DateTime` on the wire —
 * `2026-09-02T17:00:00Z` — so the comparison could not match once, every event fell out of every
 * week, and the card either degraded to "no dated sessions" or drew the run as "0 z 0" beside a
 * session it had just told the trainer was missed. The placement rule already existed, in
 * `pages/client-calendar`; it was never asked. It now lives in `entities/calendar-event` and this
 * module calls it, which is also why a session done early or late lands on the day it HAPPENED.
 *
 * Pure: no React, no DOM, no query hook, no i18next. Weekday names and dates are the surface's,
 * through `t` and `Intl`.
 *
 * ── A client with no dated sessions degrades to a SENTENCE, never to an empty grid ───────────
 * A self-paced plan schedules nothing (self-paced-plan R15), so its client has no calendar events
 * at all in any window. Seven blank squares under the heading "Ten tydzień" is a statement, and a
 * false one: it says the client was given nothing to do this week, when what happened is that this
 * client is never given days. The strip therefore has two shapes, and which one it takes is a
 * decision taken here rather than a branch on `days.length` in a component.
 *
 * ── Attendance counts SCHEDULED WORK, on `entities/calendar-event`'s own rule ────────────────
 * Done, missed, no-show, live and still-upcoming stand in the denominator; withdrawn work — a
 * cancellation, a late cancellation, a decline — stands in neither, because it was removed from the
 * prescription. An empty slot has no content to attend. That rule lives in the entity because the
 * client calendar draws from it too, and a second copy here would eventually disagree with it.
 */

import {
  occurrenceIsScheduledWork,
  occurrencePlacementDay,
  occurrenceStatusOf,
  occurrenceWasDone,
  occurrenceWasMissed,
  type OccurrencePlacement,
  type OccurrenceStatusKey,
} from '~/entities/calendar-event';
import { addDays, mondayOf, parseIsoDate, toIsoDate } from '~/shared/lib/calendarDate';

const DAYS_PER_WEEK = 7;

/** How many weeks of attendance the card states: the current one and the three before it. */
export const ATTENDANCE_WEEKS = 4;

/**
 * One occurrence, in the only terms this module needs.
 *
 * It carries the PLACEMENT fields verbatim rather than a pre-computed day: which day an occurrence
 * belongs to is R17's decision and `entities/calendar-event` owns it, and a caller that resolved it
 * on the way in would be the second implementation of the rule that has already gone wrong once.
 */
export interface ClientWeekEvent extends OccurrencePlacement {
  readonly status: string | null;
  readonly derivedStatus: string | null;
  /** The plan day's name, where one named it. */
  readonly sessionName: string | null;
}

/** One square. */
export interface ClientWeekDay {
  readonly day: string;
  /** Monday is 1. The surface reads its own weekday name off this. */
  readonly weekday: number;
  /** `null` for a day with nothing on it — a rest day, as far as this client's calendar knows. */
  readonly status: OccurrenceStatusKey | null;
  /** The session's name, or `''` where the calendar named none. */
  readonly title: string;
  readonly isToday: boolean;
  /** This is the day the TASK is about. Exactly one square can carry it, and often none does. */
  readonly isTaskDay: boolean;
}

/** One week of the attendance run. */
export interface AttendanceWeek {
  readonly startDay: string;
  readonly done: number;
  readonly total: number;
  /**
   * The week "today" falls in — which, since the strip anchors on the TASK's week, is often none of
   * the four. The surface names it "ten tydzień"; the others are named by their date, and a run
   * ending three weeks ago must not have "ten tydzień" at the bottom of it.
   */
  readonly isCurrent: boolean;
}

/**
 * What the card draws: a strip, or one sentence.
 *
 * A discriminated union rather than a strip with an `empty` flag, so a component cannot render
 * seven squares and a sentence at the same time.
 */
export type ClientWeek =
  | {
      readonly kind: 'strip';
      readonly days: readonly ClientWeekDay[];
      readonly weeks: readonly AttendanceWeek[];
    }
  | { readonly kind: 'undated' };

/** The Monday of the week containing the civil day `day`; `null` where it is not one. */
export function mondayOfDay(day: string): Date | null {
  const date = parseIsoDate(day);
  return date === null ? null : mondayOf(date);
}

/**
 * The first and last civil day the card needs — four whole Monday weeks ending on the Sunday of the
 * ANCHOR week. Hand this to `entities/calendar-event`'s `scheduleWindow`.
 *
 * It takes the same two days `clientWeek` does, and for the same reason: a window built around
 * today would not contain the week a three-week-old miss happened in, so the strip would be drawn
 * from rows that were never fetched.
 */
export function clientWeekSpan(
  todayIso: string,
  taskDay: string | null = null,
): { readonly first: string; readonly last: string } {
  const monday = anchorMonday(todayIso, taskDay);
  if (monday === null) return { first: todayIso, last: todayIso };
  return {
    first: toIsoDate(addDays(monday, -DAYS_PER_WEEK * (ATTENDANCE_WEEKS - 1))),
    last: toIsoDate(addDays(monday, DAYS_PER_WEEK - 1)),
  };
}

/**
 * Build the card's week.
 *
 * @param taskDay the client's own civil day the task is about (`sessionLocalDay`), or `null` where
 *   the reason names none — `lapsedActivity` and `noUpcomingSessions` never do.
 */
export function clientWeek(
  events: readonly ClientWeekEvent[],
  todayIso: string,
  taskDay: string | null,
): ClientWeek {
  const monday = anchorMonday(todayIso, taskDay);
  const currentMonday = mondayOfDay(todayIso);
  if (monday === null) return { kind: 'undated' };
  const currentStart = currentMonday === null ? null : toIsoDate(currentMonday);

  const placed: { readonly day: string; readonly event: ClientWeekEvent }[] = [];
  for (const event of events) {
    // The reader's own clock — the day every other square on this strip is read on.
    const day = occurrencePlacementDay(event, null);
    if (day !== null) placed.push({ day, event });
  }
  // Nothing dated ANYWHERE in the four weeks read. A self-paced client, or one whose coaching has
  // not started. Seven empty squares would say something false about this week.
  if (placed.length === 0) return { kind: 'undated' };

  const byDay = new Map<string, ClientWeekEvent[]>();
  for (const { day, event } of placed) {
    const bucket = byDay.get(day);
    if (bucket === undefined) byDay.set(day, [event]);
    else bucket.push(event);
  }

  const days: ClientWeekDay[] = [];
  for (let step = 0; step < DAYS_PER_WEEK; step += 1) {
    const day = toIsoDate(addDays(monday, step));
    // The day's loudest event: something that happened beats something upcoming, so a day holding a
    // finished session and a cancelled one reads as trained.
    const chosen = pickOfDay(byDay.get(day) ?? []);
    days.push({
      day,
      weekday: step + 1,
      status: chosen === null ? null : occurrenceStatusOf(chosen),
      title: chosen?.sessionName ?? '',
      isToday: day === todayIso,
      isTaskDay: taskDay !== null && day === taskDay,
    });
  }

  const weeks: AttendanceWeek[] = [];
  for (let back = ATTENDANCE_WEEKS - 1; back >= 0; back -= 1) {
    const start = addDays(monday, -DAYS_PER_WEEK * back);
    const startDay = toIsoDate(start);
    let done = 0;
    let total = 0;
    for (let step = 0; step < DAYS_PER_WEEK; step += 1) {
      for (const event of byDay.get(toIsoDate(addDays(start, step))) ?? []) {
        const key = occurrenceStatusOf(event);
        if (!occurrenceIsScheduledWork(key)) continue;
        total += 1;
        if (occurrenceWasDone(key)) done += 1;
      }
    }
    weeks.push({ startDay, done, total, isCurrent: startDay === currentStart });
  }

  return { kind: 'strip', days, weeks };
}

/** Which week the strip stands in, and whether it is the one a heading may call "this week". */
export interface ClientWeekAnchor {
  /** The Monday the seven squares start on. */
  readonly startDay: string;
  /** The last of the seven, so a heading can name the span without doing date maths itself. */
  readonly endDay: string;
  readonly isCurrentWeek: boolean;
}

/**
 * The week the card is about: the week of the day the TASK names, or the current one where the
 * reason names no day.
 *
 * The heading is worded from this and so is the read's window, so the two cannot disagree — and the
 * heading is settled BEFORE the schedule answers, which is why it does not change under the
 * trainer when the read lands.
 *
 * An unparseable task day falls back to today's week rather than to nothing: a malformed value on
 * one card must not take the whole strip off the screen.
 */
export function clientWeekAnchor(todayIso: string, taskDay: string | null): ClientWeekAnchor | null {
  const monday = anchorMonday(todayIso, taskDay);
  const current = mondayOfDay(todayIso);
  if (monday === null || current === null) return null;
  return {
    startDay: toIsoDate(monday),
    endDay: toIsoDate(addDays(monday, DAYS_PER_WEEK - 1)),
    isCurrentWeek: toIsoDate(monday) === toIsoDate(current),
  };
}

function anchorMonday(todayIso: string, taskDay: string | null): Date | null {
  return (taskDay === null ? null : mondayOfDay(taskDay)) ?? mondayOfDay(todayIso);
}

/**
 * Which of a day's events the square draws.
 *
 * A finished session first, then a missed one, then whatever else is there. A day almost always
 * holds one event; where it holds two, what the trainer needs to see is the strongest statement
 * about whether the client trained.
 */
function pickOfDay(events: readonly ClientWeekEvent[]): ClientWeekEvent | null {
  if (events.length === 0) return null;
  const done = events.find((event) => occurrenceWasDone(occurrenceStatusOf(event)));
  if (done !== undefined) return done;
  const missed = events.find((event) => occurrenceWasMissed(occurrenceStatusOf(event)));
  return missed ?? events[0] ?? null;
}
